import React, { createContext, useContext, useState, useEffect } from 'react'
import { useAuth } from './AuthContext'

interface PersonalSetupContextType {
  currentStep: number
  totalSteps: number
  isComplete: boolean
  skipWizard: () => void
  nextStep: () => void
  previousStep: () => void
  completeSetup: () => void
  shouldShowWizard: boolean
}

const PersonalSetupContext = createContext<PersonalSetupContextType | undefined>(undefined)

export function PersonalSetupProvider({ children }: { children: React.ReactNode }) {
  const { user, profile, isAdmin } = useAuth()
  const [currentStep, setCurrentStep] = useState(0)
  const [isComplete, setIsComplete] = useState(false)
  const [shouldShowWizard, setShouldShowWizard] = useState(false)

  const totalSteps = 3 // Welcome, Profile Info, Preferences

  useEffect(() => {
    // DISABLED - Personal setup wizard is disabled in favor of AI Setup at /welcome
    // Users can update their profile info in Settings later
    setIsComplete(true)
    setShouldShowWizard(false)
  }, [user, profile])

  const skipWizard = () => {
    if (user?.id) {
      localStorage.setItem(`personal_setup_skipped_${user.id}`, 'true')
    }
    setShouldShowWizard(false)
    setIsComplete(true)
  }

  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const completeSetup = () => {
    if (user?.id) {
      localStorage.setItem(`personal_setup_complete_${user.id}`, 'true')
    }
    setIsComplete(true)
    setShouldShowWizard(false)
  }

  return (
    <PersonalSetupContext.Provider
      value={{
        currentStep,
        totalSteps,
        isComplete,
        skipWizard,
        nextStep,
        previousStep,
        completeSetup,
        shouldShowWizard
      }}
    >
      {children}
    </PersonalSetupContext.Provider>
  )
}

export function usePersonalSetup() {
  const context = useContext(PersonalSetupContext)
  if (!context) {
    throw new Error('usePersonalSetup must be used within PersonalSetupProvider')
  }
  return context
}